// Company viewer ("2nd auctioneer") service.
// A channel partner attaches one company contact to an auction it created; that
// contact gets a view-only live panel for that auction and nothing else.
//
// Endpoints (all authenticated):
//  Channel partner:
//    GET    /api/auction-viewers/auction/:auctionId
//    POST   /api/auction-viewers/auction/:auctionId
//    DELETE /api/auction-viewers/auction/:auctionId
//  Company viewer:
//    GET    /api/auction-viewers/shared
//    GET    /api/auction-viewers/shared/:auctionId/live

import { API_BASE_URL } from './apiConfig';

const BASE = `${API_BASE_URL}/auction-viewers`;

export interface AuctionViewerInput {
  email: string;
}

export interface AuctionViewerRecord {
  id: number;
  auction_id: number;
  user_id: number | null;
  phone_number: string;
  person_name: string | null;
  company_name: string | null;
  email: string | null;
}

export type NotificationStatus = 'sent' | 'failed' | 'skipped_no_email' | null;

export interface SetViewerResult {
  viewer: AuctionViewerRecord;
  accountCreated: boolean;
  notifications: { sms: NotificationStatus; email: NotificationStatus };
}

export interface SharedAuctionSummary {
  id: number;
  auction_no: string;
  title: string;
  description: string | null;
  status: 'draft' | 'upcoming' | 'live' | 'completed' | 'cancelled';
  auction_date: string;
  start_time: string;
  end_time: string | null;
  duration: number;
  currency: string;
  partner_company: string | null;
  partner_person: string | null;
}

export interface LeaderboardEntry {
  rank: number;
  person_name: string | null;
  company_name: string | null;
  amount: string;
  bid_time: string;
  total_bids: number;
}

export interface SharedLiveAuction {
  id: number;
  auction_no: string;
  title: string;
  description: string | null;
  status: 'draft' | 'upcoming' | 'live' | 'completed' | 'cancelled';
  auction_date: string;
  start_time: string;
  end_time: string | null;
  duration: number;
  currency: string;
  auction_mode?: 'reverse' | 'forward';
  seconds_remaining: number;
  managed_by: { company_name: string; person_name: string } | null;
  winner: { person_name: string | null; company_name: string | null; amount: string } | null;
  leaderboard: LeaderboardEntry[];
  statistics: {
    total_participants: number;
    total_bids: number;
    bidders: number;
    lowest_bid: number | null;
    best_bid: number | null;
  };
}

function headers(): HeadersInit {
  const h: HeadersInit = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const token = localStorage.getItem('authToken');
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
}

async function parse(res: Response): Promise<any> {
  const body = await res.json().catch(() => null);
  if (!res.ok || !body || body.success === false) {
    throw new Error(body?.message || `Request failed (${res.status})`);
  }
  return body;
}

// Matches the 403 the API sends once a shared auction has finished.
export const AUCTION_ENDED_MESSAGE = 'This auction has ended. View access is no longer available.';

class AuctionViewerService {
  async getForAuction(auctionId: number | string): Promise<AuctionViewerRecord | null> {
    const body = await parse(await fetch(`${BASE}/auction/${auctionId}`, { headers: headers() }));
    return body.data;
  }

  async setForAuction(auctionId: number | string, input: AuctionViewerInput): Promise<SetViewerResult> {
    const body = await parse(
      await fetch(`${BASE}/auction/${auctionId}`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(input),
      })
    );
    return {
      viewer: body.data,
      accountCreated: !!body.account_created,
      notifications: body.notifications || { sms: null, email: null },
    };
  }

  async removeForAuction(auctionId: number | string): Promise<void> {
    await parse(await fetch(`${BASE}/auction/${auctionId}`, { method: 'DELETE', headers: headers() }));
  }

  async getShared(): Promise<SharedAuctionSummary[]> {
    const body = await parse(await fetch(`${BASE}/shared`, { headers: headers() }));
    return body.auctions;
  }

  async getSharedLive(auctionId: number | string): Promise<SharedLiveAuction> {
    const body = await parse(await fetch(`${BASE}/shared/${auctionId}/live`, { headers: headers() }));
    return body.auction;
  }
}

export default new AuctionViewerService();
